'use client';

import { useEffect, useImperativeHandle, useRef, forwardRef } from 'react';
import { ensureRecaptchaLoaded, RECAPTCHA_SITE_KEY, isRecaptchaEnabled } from '@/shared/utils/recaptcha';

export interface RecaptchaCheckboxHandle {
  /** Reseta o widget (chama apos submit falhar pra usuario marcar de novo). */
  reset: () => void;
  /** Pega o token atual (vazio se nao marcado). */
  getToken: () => string;
}

interface Props {
  onChange?: (token: string) => void;
  theme?: 'light' | 'dark';
  className?: string;
}

/**
 * reCAPTCHA v2 checkbox ("Nao sou um robo"). Em modo dev (sem VITE_RECAPTCHA_SITE_KEY)
 * renderiza nada e onChange dispara imediatamente com '' (backend tem flag pra aceitar).
 */
const RecaptchaCheckbox = forwardRef<RecaptchaCheckboxHandle, Props>(function RecaptchaCheckbox(
  { onChange, theme = 'dark', className },
  ref,
) {
  const divRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<number | null>(null);

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (widgetIdRef.current != null && window.grecaptcha?.reset) {
        window.grecaptcha.reset(widgetIdRef.current);
        onChange?.('');
      }
    },
    getToken: () => {
      if (widgetIdRef.current != null && window.grecaptcha?.getResponse) {
        return window.grecaptcha.getResponse(widgetIdRef.current);
      }
      return '';
    },
  }), [onChange]);

  useEffect(() => {
    if (!isRecaptchaEnabled()) {
      // Modo dev — finge que ja passou
      onChange?.('');
      return;
    }
    let cancelled = false;
    ensureRecaptchaLoaded().then(() => {
      if (cancelled || !divRef.current || !window.grecaptcha?.render) return;
      // evita render duplo (StrictMode + hot reload)
      if (widgetIdRef.current != null) return;
      widgetIdRef.current = window.grecaptcha.render(divRef.current, {
        sitekey: RECAPTCHA_SITE_KEY,
        theme,
        size: 'normal',
        callback: (token) => onChange?.(token),
        'expired-callback': () => onChange?.(''),
        'error-callback': () => onChange?.(''),
      });
    }).catch(() => {
      // sem captcha disponivel — backend aplica seu proprio gate
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme]);

  if (!isRecaptchaEnabled()) return null;
  // Wrapper com overflow hidden + rounded pra esconder a borda branca padrao do widget.
  // Tambem aplica color-scheme: dark pra evitar destaque de focus do browser.
  return (
    <div
      className={`inline-block overflow-hidden rounded-lg ${className ?? ''}`}
      style={{ colorScheme: 'dark' }}
    >
      <div ref={divRef} />
    </div>
  );
});

export default RecaptchaCheckbox;
